import { Controller } from "@hotwired/stimulus";
import { I18n } from "./../helpers/i18n";

// Connects to data-controller="roi-calculator"
export default class extends Controller {
  static values = {
    currency: String,
    locale: String,
  };

  static targets = [
    "revenue",
    "winRate",
    "tenderUsers",
    "officeUsers",
    "tendersCount",
    "contactUsers",
    "planRadio",
    "frequencyRadio",
    "displayMonthlyPrice",
    "displayYearlyPrice",
    "totalUsersWarning",
    "maxUsersWarning",
    "activeTendersWarning",
    "maxTendersWarning",
    "userAlert",
    "tenderAlert",
    "totalBidAmount",
    "averageBidAmount",
    "averageBidDuration",
    "monthlyCost",
    "totalBidCount",
    "costDetails",
    "appUsersCost",
    "appUsersCount",
    "pricePerUser",
    "totalAppUsersPrice",
    "contactUsersCost",
    "contactUsersCount",
    "priceContact",
    "totalContactUsersPrice",
    "timeSaved",
    "daysSaved",
    "yearlyHours",
    "yearlyDays",
    "additionalTenders",
    "additionalRevenue",
    "revenueIncrease",
    "createListing",
    "manageListing",
    "manageCertification",
    "updateListing",
    "updateResumes",
    "updateReferences",
    "partnerSeeking",
    "clientAnalysis",
    "managePlanning",
    "managePerformance",
    "actionPlan",
  ];

  connect() {
    this.HOURS_PER_DAY = 7; // Heures de travail par jour
    this.WEEKS_PER_MONTH = 4; // Simplification : 1 mois = 4 semaines
    this.MONTH_PER_YEAR = 12; // Simplification : 1 année = 12 mois
    this.DAYS_PER_MONTH = this.WEEKS_PER_MONTH * 5;
    this.BID_SELECTION_FACTOR = 0.3;

    this.calculate();
  }

  calculate() {
    const i18n = new I18n(this.localeValue, "Europe/Paris", this.currencyValue);
    // Récupération des valeurs
    const selectedPlan = this.planRadioTargets.find((radio) => radio.checked);
    const selectedFrequency = this.frequencyRadioTargets.find((radio) => radio.checked);

    if (!selectedPlan || !selectedFrequency) {
      return;
    }

    const revenue = parseInt(this.revenueTarget.value) || 0;
    const winRate = parseFloat(this.winRateTarget.value) / 100 || 0;
    const tenderUsersCount = parseInt(this.tenderUsersTarget.value) || 0;
    const officeUsersCount = parseInt(this.officeUsersTarget.value) || 0;
    const contactUsersCount = parseInt(this.contactUsersTarget.value) || 0;
    const appUsersCount = tenderUsersCount + officeUsersCount;
    const monthlyTendersCount = parseInt(this.tendersCountTarget.value) || 0;
    const yearlyTendersCount = monthlyTendersCount * this.MONTH_PER_YEAR || 0;
    const totalBidAmount = revenue / winRate;
    const totalBidCount = yearlyTendersCount / winRate;
    const averageBidAmount = totalBidAmount / yearlyTendersCount;
    const averageBidDuration =
      (this.DAYS_PER_MONTH * this.MONTH_PER_YEAR) / (yearlyTendersCount / appUsersCount);
    const priceYearly = parseFloat(selectedPlan.dataset.priceYearly);
    const priceMonthly = parseFloat(selectedPlan.dataset.priceMonthly);
    const priceContact = parseFloat(selectedPlan.dataset.priceContact);
    const frequency = selectedFrequency.value;
    const maxUsers =
      selectedPlan.dataset.maxUsers === "null"
        ? null
        : parseInt(selectedPlan.dataset.maxUsers);
    const maxTenders =
      selectedPlan.dataset.maxTenders === "null"
        ? null
        : parseInt(selectedPlan.dataset.maxTenders);

    // Utiliser le bon prix selon la fréquence
    const pricePerUser = frequency === "yearly" ? priceYearly : priceMonthly;

    // Vérification des limites
    this.detectLimits(maxUsers, appUsersCount, maxTenders, monthlyTendersCount);

    // Analyse
    this.averageBidAmountTarget.textContent = i18n.currency(averageBidAmount);
    this.totalBidAmountTarget.textContent = i18n.currency(totalBidAmount);
    this.averageBidDurationTarget.textContent = averageBidDuration.toFixed(2);
    this.totalBidCountTarget.textContent = totalBidCount.toFixed(0);
    // Calcul du coût mensuel
    let monthlyCost = 0;
    // Coût des utilisateurs principaux (pricePerUser est déjà le prix mensuel)
    monthlyCost += appUsersCount * pricePerUser;
    // Coût des utilisateurs contact (19€/mois chacun)
    monthlyCost += contactUsersCount * priceContact;
    this.monthlyCostTarget.textContent = i18n.currency(monthlyCost);

    let totalAppUsersPrice = appUsersCount * pricePerUser;

    if (appUsersCount > 0) {
      this.appUsersCostTarget.classList.remove("hidden");
      this.appUsersCountTarget.textContent = appUsersCount;
      this.pricePerUserTarget.textContent = i18n.currency(pricePerUser);
      this.totalAppUsersPriceTarget.textContent = i18n.currency(totalAppUsersPrice);
    } else {
      this.appUsersCostTarget.classList.add("hidden");
    }
    if (contactUsersCount > 0) {
      let totalContactUsersPrice = contactUsersCount * priceContact;
      this.contactUsersCostTarget.classList.remove("hidden");
      this.contactUsersCountTarget.textContent = contactUsersCount;
      this.priceContactTarget.textContent = i18n.currency(priceContact);
      this.totalContactUsersPriceTarget.textContent =
        i18n.currency(totalContactUsersPrice);
    } else {
      this.contactUsersCostTarget.classList.add("hidden");
    }

    // Mise à jour du prix mensuel affiché
    this.updateUIMonthlyPricingDisplay(frequency);

    // Calcul des économies de temps
    const timeSavings = this.calculateTimeSavings(
      appUsersCount,
      officeUsersCount,
      tenderUsersCount,
      selectedPlan,
    );

    this.updateUITimeSavingDetails(timeSavings);

    // Total mensuel
    const totalHoursPerMonth = timeSavings.total;
    const totalDaysPerMonth = totalHoursPerMonth / this.HOURS_PER_DAY;

    this.timeSavedTarget.textContent = totalHoursPerMonth.toFixed(1);
    this.daysSavedTarget.textContent = totalDaysPerMonth.toFixed(1);

    // Calculs annuels
    const yearlyHours = totalHoursPerMonth * this.MONTH_PER_YEAR;
    const yearlyDays = totalDaysPerMonth * this.MONTH_PER_YEAR;
    const additionalTenders = Math.floor(yearlyDays / averageBidDuration);
    const yearly_cost = monthlyCost * this.MONTH_PER_YEAR;
    const additionalRevenue =
      additionalTenders * averageBidAmount * winRate - yearly_cost;
    const revenueIncrease = (additionalRevenue / revenue) * 100;

    this.yearlyHoursTarget.textContent = yearlyHours.toFixed(0);
    this.yearlyDaysTarget.textContent = yearlyDays.toFixed(1);
    this.additionalTendersTarget.textContent = additionalTenders.toFixed(0);
    this.additionalRevenueTarget.textContent = i18n.currency(additionalRevenue);
    this.revenueIncreaseTarget.textContent = `+ ${revenueIncrease.toFixed(1)} %`;
  }

  // Mise à jour de l'affichage des économies détaillées
  updateUITimeSavingDetails(timeSavings) {
    this.updateAndActivateLine(timeSavings.createListing, this.createListingTarget);
    this.updateAndActivateLine(timeSavings.manageListing, this.manageListingTarget);
    this.updateAndActivateLine(
      timeSavings.manageCertification,
      this.manageCertificationTarget,
    );
    this.updateAndActivateLine(timeSavings.updateListing, this.updateListingTarget);
    this.updateAndActivateLine(timeSavings.updateResumes, this.updateResumesTarget);
    this.updateAndActivateLine(timeSavings.updateReferences, this.updateReferencesTarget);
    this.updateAndActivateLine(timeSavings.clientAnalysis, this.clientAnalysisTarget);
    this.updateAndActivateLine(timeSavings.partnerSeeking, this.partnerSeekingTarget);
    this.updateAndActivateLine(timeSavings.managePlanning, this.managePlanningTarget);
    this.updateAndActivateLine(
      timeSavings.managePerformance,
      this.managePerformanceTarget,
    );
    this.updateAndActivateLine(timeSavings.actionPlan, this.actionPlanTarget);
  }

  updateAndActivateLine(saving, target) {
    if (saving.toFixed(1) > 0) {
      target.textContent = saving.toFixed(0);
      target.dataset.active = true;
    } else {
      target.textContent = " - ";
      target.dataset.active = false;
    }
  }

  detectLimits(maxUsers, appUsersCount, maxTenders, monthlyTendersCount) {
    let activeTenders = (monthlyTendersCount / this.BID_SELECTION_FACTOR).toFixed(0); // plus d'affaire sont ouverte que d'affaires étudiées

    if (maxUsers !== null && appUsersCount > maxUsers) {
      this.userAlertTarget.classList.remove("hidden");
      this.totalUsersWarningTarget.textContent = appUsersCount;
      this.maxUsersWarningTarget.textContent = maxUsers;
    } else {
      this.userAlertTarget.classList.add("hidden");
    }

    if (maxTenders !== null && activeTenders > maxTenders) {
      this.tenderAlertTarget.classList.remove("hidden");
      this.activeTendersWarningTarget.textContent = activeTenders;
      this.maxTendersWarningTarget.textContent = maxTenders;
    } else {
      this.tenderAlertTarget.classList.add("hidden");
    }
  }

  updateUIMonthlyPricingDisplay(frequency) {
    if (frequency === "yearly") {
      this.displayYearlyPriceTargets.forEach((element) =>
        element.classList.replace("hidden", "block"),
      );
      this.displayMonthlyPriceTargets.forEach((element) =>
        element.classList.replace("block", "hidden"),
      );
    } else if (frequency === "monthly") {
      this.displayYearlyPriceTargets.forEach((element) =>
        element.classList.replace("block", "hidden"),
      );
      this.displayMonthlyPriceTargets.forEach((element) =>
        element.classList.replace("hidden", "block"),
      );
    }
  }

  calculateTimeSavings(appUsersCount, officeUsersCount, tenderUsersCount, selectedPlan) {
    const managers = Math.ceil(appUsersCount / 3); // 1 manager pour 3 utilisateurs

    // Calculs en heures par mois (1 mois = 4 semaines)
    const savings = {
      // Secrétariat
      createListing: 0.5 * this.WEEKS_PER_MONTH * officeUsersCount, // 0.5h/semaine × 4 semaines
      manageListing: 0.5 * this.WEEKS_PER_MONTH * officeUsersCount, // 1h/semaine × 4 semaines
      manageCertification: 1 * officeUsersCount,

      // Utilisateurs
      updateListing: 1 * this.WEEKS_PER_MONTH * tenderUsersCount, // 1h/semaine × 4 semaines
      updateResumes: 1 * tenderUsersCount,
      updateReferences: 2 * tenderUsersCount,
      partnerSeeking: 1 * tenderUsersCount, // 1h/mois
      clientAnalysis: 2 * tenderUsersCount, // 2h/mois

      // Managers
      managePlanning: 1 * this.WEEKS_PER_MONTH * managers, // 1h/semaine × 4 semaines
      managePerformance: 1 * this.HOURS_PER_DAY * managers, // 1j/mois = 7h/mois
      actionPlan: (3 / this.MONTH_PER_YEAR) * managers, // 1j/mois = 7h/mois
    };

    switch (selectedPlan.value) {
      case "core":
        savings.updateResumes = 0;
        savings.updateReferences = 0;
        savings.clientAnalysis = 0;
        savings.partnerSeeking = 0;
        savings.managePlanning = 0;
        savings.managePerformance = 0;
        savings.actionPlan = 0;
        break;
      case "pro":
        savings.actionPlan = 0;
        break;
      case "performance":
        break;
    }

    // Calcul du total
    savings.total = Object.values(savings).reduce((sum, value) => sum + value, 0);

    return savings;
  }
}
